import {Component, OnDestroy, OnInit} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthorizationService } from '@unleashed/services/account';
import {combineLatest, Subscription} from 'rxjs';
import {BusyService} from '@unleashed/services/common';
import {take} from 'rxjs/operators';
import {BookingSessionService} from '@unleashed/services/booking';
import {Booking, BookingConfiguration, GuestOfHonor, NavigationSteps} from '@unleashed/models/booking';
import {maxDateValidator, minDateValidator} from '@unleashed/common/validators';
import {IMyDate} from 'angular-mydatepicker';
import {faMinus, faPlus} from '@fortawesome/free-solid-svg-icons';
import {User} from '@unleashed/models/account';
import {getInputDate} from '@unleashed/common/utils';

@Component({
  selector: 'ua-wizard-guest-info-step',
  templateUrl: './wizard-guest-info-step.component.html',
  styleUrls: ['./wizard-guest-info-step.component.scss']
})
export class WizardGuestInfoComponent implements OnInit, OnDestroy {

  public readonly faPlus = faPlus;
  public readonly faMinus = faMinus;

  booking?: Booking;
  configuration?: BookingConfiguration;
  bookingForm: FormGroup;
  formDirty = false;
  user?: User;

  subscriptions = new Subscription();

  today = this.myDateFromDate(new Date());

  constructor(
    private authService: AuthorizationService,
    private formBuilder: FormBuilder,
    private busyService: BusyService,
    private sessionService: BookingSessionService,
  ) {
    this.bookingForm = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.busyService.set('start', true);

    this.subscriptions.add(
      combineLatest([
        this.authService.getUser(),
        this.sessionService.getConfig()
      ]).subscribe(([user, config]) => {
          this.user = user;
          this.setConfig(config);
          this.busyService.set('start', false);
        },
          err => {
          this.busyService.set('start', false);
        }
      )
    );

    this.subscriptions.add(
      this.sessionService.getBooking()
        .pipe(take(1))
        .subscribe(booking => {
          this.setBooking(booking);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setBooking(booking: Booking): void {
    this.booking = booking;

    let isDefault = false;
    if (booking.guestsOfHonor.length === 0) {
      isDefault = true;
      booking.guestsOfHonor = [...this.sessionService.defaultBooking.guestsOfHonor];
    }
    this.bookingForm = this.formBuilder.group({
      guestsOfHonor: this.formBuilder.array([]),
    });

    booking.guestsOfHonor.forEach(g => {
      this.addGuestOfHonorGroup(booking.isDefault || isDefault ? undefined : g);
    });
  }

  setConfig(config: BookingConfiguration): void {
    this.configuration = config;
  }

  addGuestOfHonorGroup(guestOfHonor?: GuestOfHonor): void {
    if (this.guestsOfHonorCount > this.maxGuestsOfHonor) {
      return;
    }

    const minDateOfBirth = new Date();
    minDateOfBirth.setFullYear(minDateOfBirth.getFullYear() - 120);

    const group = this.formBuilder.group({
      firstName: [guestOfHonor?.firstName ?? '', Validators.required],
      lastName: [guestOfHonor?.lastName ?? '', Validators.required],
      dateOfBirth: [
        guestOfHonor?.dateOfBirth ? `${(guestOfHonor?.dateOfBirth.getMonth() + 1).toString().padStart(2, '0')}/${(guestOfHonor?.dateOfBirth.getDate()).toString().padStart(2, '0')}/${guestOfHonor?.dateOfBirth.getFullYear()}` : '',
        [
          Validators.required,
          maxDateValidator(new Date(this.today.year, this.today.month - 1, this.today.day)),
          minDateValidator(minDateOfBirth)
        ]]
    });

    this.guestsOfHonorFormArray.push(group);
  }

  removeGuestOfHonorGroup(): void {
    if (this.guestsOfHonorCount === 1) {
      return;
    }

    this.guestsOfHonorFormArray.removeAt(this.guestsOfHonorCount - 1);
  }

  get guestsOfHonorCount(): number {
    return this.guestsOfHonorFormArray.length;
  }

  get maxGuestsOfHonor(): number {
    return this.configuration?.maxGuestsOfHonor ?? 2;
  }

  get guestsOfHonorFormArray(): FormArray {
    return this.bookingForm.controls.guestsOfHonor as FormArray;
  }

  get guestsOfHonorFormGroups(): FormGroup[] {
    return this.guestsOfHonorFormArray.controls as FormGroup[];
  }

  myDateFromDate(date: Date): IMyDate {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };
  }

  stepChange(step: number): void {
    switch (step) {
      case 1:
        this.sessionService.navigateToStep(NavigationSteps.Start);
        break;
      case 2:
        this.sessionService.navigateToStep(NavigationSteps.Review);
        break;
      case 3:
        break;
    }
  }

  onLoginSucess(): void {
    this.formDirty = true;
    if (this.booking && this.bookingForm.valid) {
      const booking: Booking = {
        ...this.booking,
        guestsOfHonor: this.bookingForm.value.guestsOfHonor.map((m: any) => ({
          firstName: m.firstName,
          lastName: m.lastName,
          dateOfBirth: getInputDate(m.dateOfBirth)
        })),
        accountId: this.user?.accountId,
      };

      this.sessionService.upsertCart(booking)
        .subscribe(
          _ => {
            this.sessionService.navigateToStep(NavigationSteps.Checkout);
            this.busyService.set('start-save', false);
          },
          err => {
            this.busyService.set('start-save', false);
          });
    }
  }
}
